/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2025 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers.  The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import { xPartNumberStyle } from "@eisinc/App/SharedStyles/ProductPartNumberStyle";
import Tooltip, { TooltipPresentationProps } from "@eisinc/MobiusOverrides/Tooltip/Tooltip";
import { UnitOfMeasureData } from "@eisinc/Overrides/Components/ProductQuantityOrdered";
import LinkInfo from "@eisinc/XDL/Icons/LinkInfo";
import { color } from "@eisinc/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import {
    HasProduct,
    HasProductContext,
    withProduct,
    withProductContext,
} from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { css } from "styled-components";

export interface ProductQtyInfoProps {
    extendedStyles?: ProductQtyInfoStyles;
}

type Props = ProductQtyInfoProps & HasProduct & HasProductContext;

export interface ProductQtyInfoStyles {
    qtyInfoContainer?: InjectableCss;
    qtyInfoItem?: InjectableCss;
    labelText?: TypographyProps;
    valueText?: TypographyProps;
    packDescription?: InjectableCss;
    tooltip?: TooltipPresentationProps;
}

export const productQtyInfoStyles: ProductQtyInfoStyles = {
    qtyInfoContainer: {
        css: css`
            display: flex;
            align-items: center;
            padding-top: 12px;
            border-top: 1px solid ${color("common.border")};
            flex-wrap: wrap;
            row-gap: 12px;
        `,
    },
    qtyInfoItem: {
        css: css`
            display: flex;
            align-items: center;
            :not(:last-child) {
                padding-right: 16px;
                margin-right: 16px;
                border-right: 1px solid ${color("common.border")};
            }
        `,
    },
    labelText: xPartNumberStyle.label,
    valueText: xPartNumberStyle.value,
    tooltip: {
        iconProps: {
            src: LinkInfo,
            color: "primary.main",
            css: css`
                line-height: normal;
                margin-left: 8px;
                margin-right: 0;
            `,
        },
        cssOverrides: {
            tooltipClickable: css`
                margin-right: 0;
                line-height: normal;
            `,
        },
    },
};

const ProductQtyInfo: React.FC<Props> = ({
    product,
    productContext: {
        productInfo: { unitOfMeasure },
    },
    extendedStyles,
}) => {
    if (!product) {
        return null;
    }

    const unitOfMeasuresData: UnitOfMeasureData[] = JSON.parse(product.properties.unitOfMeasureData || "[]");
    const [minQty, setMinQty] = React.useState<string | number>(1);
    const [msq, SetMsq] = React.useState<string | number>(1);

    React.useEffect(() => {
        if (product.isVariantParent) {
            setMinQty("--");
            SetMsq("--");
        } else if (unitOfMeasuresData && unitOfMeasure) {
            const selectedUOM = unitOfMeasuresData.filter(uom => uom.UnitOfMeasure === unitOfMeasure);
            if (selectedUOM.length) {
                setMinQty(selectedUOM[0].MinimumOrderQuantity || 1);
                SetMsq(selectedUOM[0].MultipleSaleQuantity || 1);
            } else {
                setMinQty(product?.properties?.minimumOrderQty || product?.minimumOrderQty || 1);
                SetMsq(product?.properties?.multipleSaleQty || product?.detail?.multipleSaleQty || 1);
            }
        }
    }, [unitOfMeasure, product]);

    const styles = React.useMemo(() => {
        return {
            ...productQtyInfoStyles,
            ...extendedStyles,
        };
    }, [extendedStyles]);

    return (
        <StyledWrapper {...styles.qtyInfoContainer}>
            <StyledWrapper {...styles.qtyInfoItem} as="span">
                <Typography {...styles.labelText}>{translate("Min QTY")}</Typography>
                <Typography {...styles.valueText}>{minQty}</Typography>
            </StyledWrapper>
            <StyledWrapper {...styles.qtyInfoItem} as="span">
                <Typography {...styles.labelText}>{translate("QTY Increment")}</Typography>
                <Typography {...styles.valueText}>{msq}</Typography>
                <Tooltip {...styles.tooltip} text={siteMessage("Custom_MSQ_Help_Bubble") as string} />
            </StyledWrapper>
            {(product.packDescription || product.isVariantParent) && (
                <StyledWrapper {...styles.qtyInfoItem} as="span">
                    <Typography {...styles.labelText}>{translate("Pack")}</Typography>
                    <Typography {...styles.valueText}>
                        {product.isVariantParent ? "--" : product.packDescription}
                    </Typography>
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

export default withProductContext(withProduct(ProductQtyInfo));
